/**
 * Minimal EXIF orientation reader.
 *
 * We only need one tag (0x0112), so this walks the JPEG APP1/TIFF header by
 * hand rather than pulling in a full EXIF library for 40 lines of work.
 *
 * Why we need it at all: the browser MAY auto-rotate a decoded bitmap, and
 * canvas output NEVER carries EXIF forward. So after re-encoding, orientation
 * lives only in the pixels. If the browser did not rotate and we do not rotate
 * either, the receipt is permanently sideways and no amount of server-side
 * sharp.rotate() can recover it -- the tag is gone.
 */

export const ORIENTATION_NORMAL = 1;

export async function readExifOrientation(file: Blob): Promise<number | null> {
  // The marker we want lives in the first 128KB in every real-world case.
  const head = await file.slice(0, 131_072).arrayBuffer();
  const view = new DataView(head);

  if (view.byteLength < 4) return null;
  if (view.getUint16(0, false) !== 0xffd8) return null; // not a JPEG

  let offset = 2;
  while (offset + 4 <= view.byteLength) {
    const marker = view.getUint16(offset, false);
    offset += 2;

    // APP1 -- the EXIF segment.
    if (marker === 0xffe1) {
      const segmentLength = view.getUint16(offset, false);
      const exifStart = offset + 2;
      if (view.getUint32(exifStart, false) !== 0x45786966) return null; // "Exif"

      const tiff = exifStart + 6;
      if (tiff + 8 > view.byteLength) return null;

      // "II" little-endian or "MM" big-endian.
      const little = view.getUint16(tiff, false) === 0x4949;
      const ifdOffset = view.getUint32(tiff + 4, little);
      const dirStart = tiff + ifdOffset;
      if (dirStart + 2 > view.byteLength) return null;

      const entries = view.getUint16(dirStart, little);
      for (let i = 0; i < entries; i += 1) {
        const entry = dirStart + 2 + i * 12;
        if (entry + 12 > view.byteLength) break;
        if (view.getUint16(entry, little) === 0x0112) {
          return view.getUint16(entry + 8, little);
        }
      }
      offset += segmentLength;
      continue;
    }

    // Start of scan: image data begins, no more metadata.
    if (marker === 0xffda || (marker & 0xff00) !== 0xff00) break;
    if (offset + 2 > view.byteLength) break;
    offset += view.getUint16(offset, false);
  }

  return null;
}

/**
 * The canvas transform for a given EXIF orientation, applied when the browser
 * did NOT already rotate for us. Width and height are the TARGET dimensions,
 * already swapped by the caller for the quarter-turn cases.
 */
export function applyOrientation(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  orientation: number,
  width: number,
  height: number,
): void {
  switch (orientation) {
    case 2:
      ctx.translate(width, 0);
      ctx.scale(-1, 1);
      break;
    case 3:
      ctx.translate(width, height);
      ctx.rotate(Math.PI);
      break;
    case 4:
      ctx.translate(0, height);
      ctx.scale(1, -1);
      break;
    case 5:
      ctx.rotate(0.5 * Math.PI);
      ctx.scale(1, -1);
      break;
    case 6:
      ctx.rotate(0.5 * Math.PI);
      ctx.translate(0, -height);
      break;
    case 7:
      ctx.rotate(0.5 * Math.PI);
      ctx.translate(width, -height);
      ctx.scale(-1, 1);
      break;
    case 8:
      ctx.rotate(-0.5 * Math.PI);
      ctx.translate(-width, 0);
      break;
    default:
      break; // 1, or unknown: nothing to do
  }
}

/**
 * Did the browser already apply the rotation when decoding?
 *
 * For a quarter-turn orientation the decoded bitmap's aspect should be the
 * INVERSE of the raw stored aspect. If the bitmap still matches the stored
 * orientation, the browser left it alone and we must rotate ourselves.
 *
 * There is no API that answers this directly, which is why it is inferred.
 */
export function browserAlreadyRotated(
  orientation: number,
  bitmapWidth: number,
  bitmapHeight: number,
  rawWidth: number,
  rawHeight: number,
): boolean {
  const quarterTurn = orientation >= 5 && orientation <= 8;
  if (!quarterTurn) return true; // nothing to infer; flips are handled below

  const bitmapIsLandscape = bitmapWidth > bitmapHeight;
  const rawIsLandscape = rawWidth > rawHeight;
  return bitmapIsLandscape !== rawIsLandscape;
}
